const { onModeEnter, onModeLeave, getMode } = require('./media.js');

/**
 * ============================================================================
 * StickyHeader — Adds/removes a class on scroll to make header sticky.
 * Also toggles a global class on <body> for page-level adjustments.
 * ============================================================================
 */
export class StickyHeader {
	/**
	 * @param {object} options - StickyHeader options.
	 * @param {string|NodeList|HTMLElement} [options.node='#header'] - Header element(s) selector or element(s).
	 * @param {number} [options.threshold=40] - Scroll threshold to trigger stickiness.
	 * @param {string} [options.bodyClass='--header-is-sticky'] - Class name added to <body> when sticky is active.
	 */
	constructor(options = {}) {
		this.options = {
			node: document.querySelectorAll('#header'),
			threshold: 60,
			bodyClass: '--header-is-sticky',
			...options
		};

		// Normalize node(s)
		this.headerNodes =
			this.options.node instanceof NodeList
				? Array.from(this.options.node)
				: this.options.node instanceof HTMLElement
					? [this.options.node]
					: Array.from(document.querySelectorAll(this.options.node));

		if (!this.headerNodes.length) {
			console.warn('StickyHeader: No header elements found.');
			return;
		}

		this._ticking = false;
		this._handleScroll = this._handleScroll.bind(this);

		document.addEventListener('scroll', this._handleScroll, { passive: true });
		this._handleScroll();
	}

	/**
	 * Handles scroll events with requestAnimationFrame throttling.
	 * @private
	 */
	_handleScroll() {
		if (this._ticking) return;
		this._ticking = true;

		requestAnimationFrame(() => {
			const scrollY = window.scrollY || window.pageYOffset;
			const isSticky = scrollY > this.options.threshold;

			// Toggle class on each header node
			this.headerNodes.forEach(el => {
				el.classList.toggle('header--sticky', isSticky);
			});

			// Toggle class on body (useful for global style adjustments)
			document.body.classList.toggle(this.options.bodyClass, isSticky);

			this._ticking = false;
		});
	}

	/**
	 * Removes the scroll event listener.
	 */
	destroy() {
		document.removeEventListener('scroll', this._handleScroll);
	}
}


/**
 * ============================================================================
 * StickyTabs — Keeps tab navigation fixed and updates active state on scroll.
 * ============================================================================
 */
export class StickyTabs {
	/**
	 * @param {object} options - StickyTabs options.
	 */
	constructor(options = {}) {
		this.options = { ...options };

		this.navTabs = $('.sticky-anchors');
		if (!this.navTabs.length || this.navTabs.hasClass('--no-js')) return;

		// Constants
		this.SINGLE_TAB_CLASS = 'is-single-tab';
		this.SCROLL_OFFSET = 55;
		this.MIN_HEIGHT = 50;

		// Cached measurements
		this.navTabsHeight = this.navTabs.outerHeight() + 1;
		this.navTabsOffset = this.navTabs.offset().top - this.SCROLL_OFFSET;
		this.lastId = null;

		// DOM collections
		this.navTabsItems = this.navTabs.find('a');
		this.scrollItems = this.navTabsItems
			.map(function () {
				const item = $($(this).attr('href'));
				return item.length ? item : null;
			})
			.get();

		this.navSections = $('.anchor-section');

		this._bindEvents();
		this._initStickyTabs();
	}

	/**
	 * Initializes sticky behavior and sets default active state.
	 * @private
	 */
	_initStickyTabs() {
		onModeEnter('desktop', () => {
			this.navTabs.css('minHeight', this.navTabsHeight);
		});

		onModeLeave('desktop', () => {
			this.navTabs.css('minHeight', this.MIN_HEIGHT);
			this.navTabs.removeClass('nav--sticky');
		});

		// Set active anchor based on current URL hash
		this._checkAnchor(this.navTabsItems, 'href');
		this._checkAnchor(this.navSections, 'id');
	}

	/**
	 * Binds scroll and click events.
	 * @private
	 */
	_bindEvents() {
		this._handleScroll = this._handleScroll.bind(this);
		document.addEventListener('scroll', this._handleScroll, { passive: true });

		if (this.navTabs.hasClass(this.SINGLE_TAB_CLASS)) {
			this._handleSingleTabClick = this._handleSingleTabClick.bind(this);
			this.navTabsItems.on('click', this._handleSingleTabClick);
		}

		this._handleScroll();
	}

	/**
	 * Handles scroll events for sticky navigation and active section detection.
	 * @private
	 */
	_handleScroll() {
		const scrollY = window.scrollY || window.pageYOffset;
		const currentMode = getMode();

		// Sticky behavior only in desktop mode
		if (currentMode === 'desktop') {
			const isSticky = scrollY > this.navTabsOffset;
			this.navTabs.toggleClass('nav--sticky', isSticky);
			this.navTabs.css('minHeight', isSticky ? this.navTabsHeight : this.MIN_HEIGHT);
		}

		// Update active tab on scroll
		if (!this.navTabs.hasClass(this.SINGLE_TAB_CLASS)) {
			const fromTop = scrollY + this.navTabsHeight;
			let currentItem = null;

			for (let i = this.scrollItems.length - 1; i >= 0; i--) {
				const item = this.scrollItems[i];
				if (item.offset().top < fromTop) {
					currentItem = item;
					break;
				}
			}

			const id = currentItem ? currentItem[0].id : '';
			if (this.lastId !== id) {
				this.lastId = id;
				this.navTabsItems.removeClass('active');
				if (id) {
					this.navTabsItems.filter(`[href="#${id}"]`).addClass('active');
				}
			}
		} else {
			// For single-tab mode
			if (scrollY < this.navTabs.offset().top + this.navTabsHeight - 1) {
				this.navSections.removeClass('with-pd');
			}
		}
	}

	/**
	 * Handles click on a single tab link.
	 * @param {Event} event - Click event.
	 * @private
	 */
	_handleSingleTabClick(event) {
		event.preventDefault();

		const $target = $(event.currentTarget);
		this.navTabsItems.removeClass('active');
		$target.addClass('active');

		const tabId = $target.attr('href').slice(1);

		this.navSections.each(function () {
			const $section = $(this);
			if ($section.attr('id') === tabId) {
				$section.addClass('active with-pd');
			} else {
				$section.removeClass('active with-pd');
			}
		});

		// Smooth scroll to target section
		const targetSection = document.getElementById(tabId);
		if (targetSection) {
			window.scrollTo({
				top: $(targetSection).offset().top - this.navTabsHeight - this.SCROLL_OFFSET,
				behavior: 'smooth'
			});
		}
	}

	/**
	 * Checks URL hash and sets active class to matching elements.
	 * @param {jQuery} elements - jQuery collection of elements to check.
	 * @param {string} attribute - Attribute to match (e.g. 'href' or 'id').
	 * @private
	 */
	_checkAnchor(elements, attribute) {
		let hasActive = false;
		elements.each(function () {
			const itemAttr = $(this).attr(attribute);
			if (window.location.hash === `#${itemAttr}`) {
				$(this).addClass('active');
				hasActive = true;
			} else {
				$(this).removeClass('active');
			}
		});

		if (!hasActive) {
			elements.eq(0).addClass('active');
		}
	}

	/**
	 * Cleans up event listeners.
	 */
	destroy() {
		document.removeEventListener('scroll', this._handleScroll);
		if (this.navTabs.hasClass(this.SINGLE_TAB_CLASS)) {
			this.navTabsItems.off('click', this._handleSingleTabClick);
		}
	}
}
